import { useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * useRealtime — opens a single SSE connection per authenticated session.
 * When the server emits an event, it dispatches a matching window CustomEvent
 * so any component can react without prop drilling.
 *
 * Event types emitted on window:
 *   pipe4:negocio_changed   — any deal created/updated/moved/won/lost
 *   pipe4:whatsapp_message  — new inbound WhatsApp message
 *   pipe4:notification      — new notification for the current user
 */
export function useRealtime() {
  const { user } = useAuth();
  const esRef = useRef(null);
  const retryRef = useRef(null);

  useEffect(() => {
    if (!user) return;

    const connect = () => {
      // Clean up any existing connection
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }

      const token = localStorage.getItem("session_token");
      const sseUrl = token ? `${API}/events?token=${token}` : `${API}/events`;
      const es = new EventSource(sseUrl);
      esRef.current = es;

      es.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data);
          if (event.type === "connected") return; // initial handshake

          // Dispatch a window event so any component can listen independently
          window.dispatchEvent(
            new CustomEvent(`pipe4:${event.type}`, { detail: event })
          );
        } catch { /* ignore parse errors */ }
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        // Reconnect after 5 seconds (browser also retries natively, this is extra safety)
        if (retryRef.current) clearTimeout(retryRef.current);
        retryRef.current = setTimeout(connect, 5000);
      };
    };

    connect();

    return () => {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      if (retryRef.current) clearTimeout(retryRef.current);
    };
  }, [user]);
}
